import { Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@elbklar/ui';
import { useAuth } from './features/auth/AuthContext';

// Layout
import { CRMLayout } from './components/layout/CRMLayout';

// Auth pages
import { LoginPage } from './features/auth/LoginPage';
import { AuthCallbackPage } from './features/auth/AuthCallbackPage';

// Feature pages
import { CustomersPage } from './features/customers/CustomersPage';
import { CustomerDetailPage } from './features/customers/CustomerDetailPage';
import { CustomerFormPage } from './features/customers/CustomerFormPage';

import { ContactsPage } from './features/contacts/ContactsPage';
import { ContactDetailPage } from './features/contacts/ContactDetailPage';
import { ContactFormPage } from './features/contacts/ContactFormPage';

import { InteractionsPage } from './features/interactions/InteractionsPage';
import { InteractionDetailPage } from './features/interactions/InteractionDetailPage';

import { LeadsPage } from './features/leads/LeadsPage';
import { LeadDetailPage } from './features/leads/LeadDetailPage';

import { PartnersPage } from './features/partners/PartnersPage';
import { PartnerDetailPage } from './features/partners/PartnerDetailPage';
import { PartnerFormPage } from './features/partners/PartnerFormPage';

import { DashboardPage } from './features/dashboard/DashboardPage';
import { TasksPage } from './features/tasks/TasksPage';
import { TaskFormPage } from './features/tasks/TaskFormPage';
import { PipelinePage } from './features/pipeline/PipelinePage';
import { EmailsPage } from './features/emails/EmailsPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { NotificationSettingsPage } from './features/settings/NotificationSettingsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />

      {/* Protected CRM routes */}
      <Route
        element={
          <ProtectedRoute>
            <CRMLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Dashboard */}
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Leads */}
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />

        {/* Customers */}
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/new" element={<CustomerFormPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/customers/:id/edit" element={<CustomerFormPage />} />

        {/* Partners */}
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/new" element={<PartnerFormPage />} />
        <Route path="/partners/:id" element={<PartnerDetailPage />} />
        <Route path="/partners/:id/edit" element={<PartnerFormPage />} />

        {/* Contacts */}
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/new" element={<ContactFormPage />} />
        <Route path="/contacts/:id" element={<ContactDetailPage />} />
        <Route path="/contacts/:id/edit" element={<ContactFormPage />} />

        {/* Interactions */}
        <Route path="/interactions" element={<InteractionsPage />} />
        <Route path="/interactions/:id" element={<InteractionDetailPage />} />

        {/* Tasks */}
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/new" element={<TaskFormPage />} />
        {/* Öffnet die Aufgabe im Sheet über der Liste (Wiedervorlage, Benachrichtigungen) */}
        <Route path="/tasks/:taskId" element={<TasksPage />} />

        {/* Pipeline */}
        <Route path="/pipeline" element={<PipelinePage />} />

        {/* E-Mails: Liste + Thread, Deep-Link aus Kontakt- und Kundenseite */}
        <Route path="/emails" element={<EmailsPage />} />
        <Route path="/emails/:threadId" element={<EmailsPage />} />

        {/* Settings (admin only - access control handled by backend) */}
        <Route path="/settings" element={<SettingsPage />} />

        {/* Benachrichtigungen: jeder User stellt sich das selbst ein */}
        <Route
          path="/settings/notifications"
          element={<NotificationSettingsPage />}
        />
      </Route>
    </Routes>
  );
}
